'use client'

import React, { FC, Suspense, useEffect, useState, useRef } from 'react'
import {
  Col,
  Container,
  Row,
  Tab,
  Nav,
  Form,
  Spinner,
  Button,
} from 'react-bootstrap'
import { Searchicon2 } from 'components/Common/Icon'
import Link from 'next/link'
import axios from 'axios'
import { useDebounce } from 'use-debounce'
import ReactPaginate from 'react-paginate'
import { formatDate } from 'lib/utils'
import { useRouter, useSearchParams } from 'next/navigation'
import { BLOG_PAGE_SIZE, blogPageHref } from 'lib/blogPagination'
import { API_URL } from 'constants/common'

interface BlogCategory {
  _id: number
  title: string
  slug: string
}
interface AuthorCategory {
  _id: number
  authorName: string
  bannerImage: string
  slug: string
}
interface BlogType {
  _id: number
  title: string
  date?: string
  description?: string | undefined
  image?: string
  slug: string
  author: string
  authors: [AuthorCategory]
  categories: [BlogCategory]
  createdOn: number
}

export interface BlogListSectionProps {
  /** 1-based page from the route (`/blog/` is 1, `/blog/page/<n>/` is n). */
  currentPage: number
  /** Server-fetched posts for `currentPage`, so they are in the initial HTML. */
  initialBlogs: { records: BlogType[]; recordsTotal: number; pageCount: number } | null
  initialCategories: BlogCategory[]
}

const maxLength = 240
const EMPTY_SEARCH_PARAMS = new URLSearchParams()

const BlogListSectionContent: FC<
  BlogListSectionProps & { searchParams: URLSearchParams }
> = ({ searchParams, currentPage, initialBlogs, initialCategories }) => {
  const router = useRouter()
  const queryCat = searchParams.get('cat')
  function createMarkup(c) {
    const strData = c.replace(/(<([^>]+)>)/gi, '')
    const limitedDescription = strData.slice(0, maxLength).concat('...')
    return { __html: limitedDescription }
  }

  const ref = useRef<null | HTMLElement>(null)
  const pagerRef = useRef<HTMLElement | null>(null)

  // ReactPaginate hard-codes role="navigation" on its <ul>, which stops it
  // being a list (its <li>s become orphans). The <nav> wrapper is the landmark.
  useEffect(() => {
    pagerRef.current?.querySelector('ul[role="navigation"]')?.removeAttribute('role')
  })

  const [blogData, setBlogData] = useState<BlogType[]>(
    initialBlogs?.records || []
  )
  const [categoryList, setCategoryList] = useState(initialCategories)
  const [getcat, setGetCat] = useState('')
  const [getCatAll, setGetCatAll] = useState('All')
  const [searchText, setSearchText] = useState('')
  const [debouncedText] = useDebounce(searchText, 800)
  const getCatDisplay = getcat || 'All'
  const [pageCount, setPageCount] = useState(initialBlogs?.pageCount || 0)
  const [recordsTotal, setRecordsTotal] = useState(
    initialBlogs?.recordsTotal || 0
  )
  const [pageSkip, setPageSkip] = useState(currentPage)
  // Search results are paged in place: the page routes have no search query,
  // so navigating to /blog/page/<n>/ would drop the typed search. The page is
  // tied to the search it belongs to, so a new search starts from page 1.
  const [searchPaging, setSearchPaging] = useState({ text: '', page: 1 })
  const searchPage =
    searchPaging.text === debouncedText ? searchPaging.page : 1
  // The server-fetched posts are unfiltered, so with a category in the URL
  // show the spinner (not the wrong posts) until the filtered fetch lands
  const [Loading, setLoading] = useState(
    !initialBlogs || Boolean(queryCat && queryCat !== 'All')
  )
  const [setQCat, setsetQCat] = useState('All')

  const pageNumberLimit = BLOG_PAGE_SIZE

  const getAllBlogs = async () => {
    setLoading(true)
    const categ = queryCat

    // The API's `skip` is a 0-based page index
    let skip = currentPage - 1
    let query = ''

    if (debouncedText) {
      query += `&search=${encodeURIComponent(debouncedText)}`
      skip = searchPage - 1
    } else {
      setPageSkip(currentPage)
    }
    if (categ) {
      query += `&category=${encodeURIComponent(categ)}`
    } else {
      query += `&category=All`
    }

    try {
      const res = await axios.get(
        `${API_URL}/api/blog/blogList?skip=${skip}&limit=${pageNumberLimit}&column=createdOn&dir=desc&tag=${query}`
      )
      setBlogData(res.data.data.records)

      const recordsTotals = res.data.data.recordsTotal
      setPageCount(Math.ceil(recordsTotals / pageNumberLimit))
      setRecordsTotal(recordsTotals)
    } catch (error) {
      setBlogData([])
      setPageCount(0)
    } finally {
      setLoading(false)
    }

    // Categories come from the server; only refetch if that request failed
    if (!categoryList.length) {
      axios
        .get(`${API_URL}/api/category/list`)
        .then((res) => setCategoryList(res.data.data || []))
        .catch(() => {})
    }
  }

  const hasFilter = Boolean(
    (queryCat && queryCat !== 'All') || debouncedText
  )

  useEffect(() => {
    const selectedCat = queryCat

    // Set the selected category in the state
    setsetQCat(selectedCat || '') // Provide a default value if it's undefined

    // The unfiltered page was already fetched on the server.
    if (!hasFilter && initialBlogs) {
      setBlogData(initialBlogs.records)
      setPageCount(initialBlogs.pageCount)
      setRecordsTotal(initialBlogs.recordsTotal)
      setPageSkip(currentPage)
      setLoading(false)
      return
    }

    getAllBlogs()
  }, [
    getcat,
    getCatAll,
    debouncedText,
    searchPage,
    queryCat,
    currentPage,
    initialBlogs,
  ])

  // useEffect(() => {
  //   getAllBlogs()
  // }, [getcat, getCatAll, debouncedText, router.query.page])

  // useEffect(() => {
  //   const queryPageSkip = parseInt(router.query.page as string, 10) || 0
  //   // Update the 'pageSkip' state with the query parameter value
  //   console.log('queryPageSkip', queryPageSkip)
  //   setPageSkip(queryPageSkip)

  //   getAllBlogs()
  // }, [getcat, getCatAll, debouncedText, router.query.page])

  const catQuery = queryCat ? `?cat=${encodeURIComponent(queryCat)}` : ''

  const changePages = async (event: { selected: number }) => {
    if (debouncedText) {
      setSearchPaging({ text: debouncedText, page: event.selected + 1 })
    } else {
      // Keep the category filter; the page itself lives in the path.
      router.push(`${blogPageHref(event.selected + 1)}${catQuery}`)
    }

    ref.current?.scrollIntoView({ behavior: 'smooth' })
  }
  return (
    <section className="blog_listing" ref={ref}>
      <Container>
        <Tab.Container id="left-tabs-example">
          <Row className="flex-md-row-reverse">
            <Col md={5}>
              <div className="blog_search_block ps-0 ps-lg-5 ms-0 ms-lg-5 mb-5 mb-lg-0">
                {/* <Form> */}
                <Form.Group className="mb-3">
                  <Form.Control
                    type="text"
                    placeholder="Search for the blog"
                    onChange={(e) => {
                      setSearchText(e.target.value)
                    }}
                  />
                </Form.Group>
                <Searchicon2 className="search_icon" />
                {/* </Form> */}
              </div>
            </Col>
            <Col md={7}>
              <Nav className="flex--column tabStyle1 gap-3">
                <Nav.Item>
                  <Nav.Link
                    className={`${
                      setQCat === 'All' || !setQCat ? 'active' : ''
                    }`}
                    onClick={() => {
                      setGetCat('All')
                      router.push(`${blogPageHref(1)}?cat=All`)
                      setLoading(true)
                    }}
                    eventKey={getCatAll}
                  >
                    {getCatAll}
                  </Nav.Link>
                </Nav.Item>
                {categoryList.map((getCat: BlogCategory) => (
                  <Nav.Item key={getCat.slug}>
                    <Nav.Link
                      className={`${getCat.slug === setQCat ? 'active' : ''}`}
                      onClick={() => {
                        setGetCat(getCat.slug)
                        router.push(`${blogPageHref(1)}?cat=${getCat.slug}`)
                        setLoading(true)
                      }}
                      eventKey={getCat.slug}
                    >
                      {getCat.title}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </Col>
          </Row>
          <Row>
            <Col sm={12} className="mt-5">
              <Tab.Content>
                <div>
                  <div className="text-center">
                    {Loading && (
                      <Button variant="dark" disabled>
                        <Spinner
                          className=""
                          animation="border"
                          role="status"
                        />
                        Loading...
                      </Button>
                    )}
                  </div>
                  <Row className="align-items-stretch blog_listing_wrap">
                    {blogData.length > 0 && Loading === false
                      ? blogData.map((getBlog: BlogType, index) => (
                          <React.Fragment key={index}>
                            <Col lg={6} className="mb-4">
                              <div className="blog_item">
                                <div className="item_image">
                                  <Link
                                    href={`/${getBlog.slug}`}
                                    className="title_sm text_black text-decoration-none"
                                  >
                                    <img
                                      src={getBlog.image}
                                      alt={getBlog.title}
                                    />
                                  </Link>

                                  <div className="post_cat">
                                    {getBlog.categories.map((category, i) => (
                                      <React.Fragment key={i}>
                                        <Link
                                          key={category.title}
                                          href={`/category/${category.slug}`}
                                          role="button"
                                          tabIndex={0}
                                          className="text-decoration-none text_black"
                                        >
                                          {category.title}
                                        </Link>
                                        {getBlog.categories.length - 1 !==
                                          i && <>, </>}
                                      </React.Fragment>
                                    ))}
                                  </div>
                                </div>
                                <div className="p-3 p-xl-4">
                                  {/* <div className="item_small_text mb-3">
                                  Written by
                                  <span className="text-primary ms-1">
                                  {getBlog.author
                                    ? getBlog.author
                                    : ' WebClues'}
                                    </span>
                                  </div> */}
                                  <h2 className="">
                                    <Link
                                      href={`/${getBlog.slug}`}
                                      className="title_xs text_black"
                                    >
                                      {getBlog.title}
                                    </Link>
                                  </h2>
                                  <div className="item_desc text_para mb-3  blog_content_limit">
                                    <div
                                      dangerouslySetInnerHTML={createMarkup(
                                        getBlog.description
                                      )}
                                    />
                                  </div>
                                  {getBlog.authors.map((author, ia) => (
                                    <React.Fragment key={ia}>
                                      <div className="item_small_img">
                                        <Link
                                          href={`/author/${author.slug}`}
                                          className="item_small_text"
                                        >
                                          <img
                                            src={author.bannerImage}
                                            alt={getBlog.title}
                                          />
                                        </Link>
                                        <Link
                                          href={`/author/${author.slug}`}
                                          className="cursor-pointer text-reset text-decoration-none"
                                        >
                                          <div className="name cursor-pointer">
                                            {author.authorName}
                                          </div>
                                        </Link>
                                        <div className="slash">|</div>
                                        {/* UTC so the server-rendered date
                                        matches the client (no hydration
                                        mismatch across timezones) */}
                                        {formatDate(getBlog.createdOn, {
                                            utc: true,
                                          })}
                                      </div>
                                    </React.Fragment>
                                  ))}
                                </div>
                              </div>
                            </Col>
                          </React.Fragment>
                        ))
                      : ''}
                  </Row>
                </div>
              </Tab.Content>
            </Col>
          </Row>
        </Tab.Container>
      </Container>

      <Container className="pt-2 pt-xl-5">
        <Row>
          <Col xs={12} className="d-flex justify-content-center pt-4">
            <div className="pagination_style1 mb-0">
              {/* nav, not <Pagination> (a <ul>): ReactPaginate renders its own list */}
              <nav aria-label="Blog pages" ref={pagerRef}>
                <ReactPaginate
                  breakLabel="..."
                  pageRangeDisplayed={5}
                  previousLabel=""
                  previousClassName="prev page-item"
                  nextLabel=""
                  nextClassName="next"
                  pageCount={pageCount || 0}
                  onPageChange={changePages}
                  // Real hrefs so crawlers can follow the pages; clicks are
                  // still handled by onPageChange. No href past either end
                  // (the disabled prev/next), nor while searching, as the
                  // page routes can't carry the search.
                  hrefBuilder={(page, total) =>
                    !debouncedText && page >= 1 && page <= total
                      ? `${blogPageHref(page)}${catQuery}`
                      : undefined
                  }
                  hrefAllControls
                  containerClassName="pagination_style1 mb-0 pagination"
                  pageClassName="page-item "
                  pageLinkClassName="page-link"
                  previousLinkClassName="page-link"
                  nextLinkClassName="page-link"
                  disabledClassName="disabled"
                  activeClassName="active"
                  forcePage={
                    debouncedText ? searchPage - 1 : pageSkip ? pageSkip - 1 : 0
                  }
                />
              </nav>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  )
}

const BlogListSectionWithParams: FC<BlogListSectionProps> = (props) => (
  <BlogListSectionContent
    {...props}
    searchParams={useSearchParams() ?? EMPTY_SEARCH_PARAMS}
  />
)

// useSearchParams needs a Suspense boundary on statically rendered pages; the
// fallback renders the same section with no query so the static HTML keeps it.
const BlogListSection: FC<BlogListSectionProps> = (props) => (
  <Suspense
    fallback={
      <BlogListSectionContent {...props} searchParams={EMPTY_SEARCH_PARAMS} />
    }
  >
    <BlogListSectionWithParams {...props} />
  </Suspense>
)

export default BlogListSection
